'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronRight, MessageCircleQuestion } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { askQuestion } from '@/lib/api';
import type { ClientPerson } from '@/lib/types';
import { Avatar } from './Avatar';
import { EventSheetBand } from './EventSheetBand';

/**
 * THE PERSON WHO ANSWERS
 * ======================
 *
 * "Questions? This event is run by Mary Reed." was a sentence at the bottom
 * of the page with nothing to do: it named somebody and then offered no way
 * to reach them. This is the shape the conference and the league already
 * use — a face, and a form that goes to them.
 *
 * Her address is never in the page. The note is relayed by apps/api, which
 * turns the event's primary contact into a mailbox at send time, so when the
 * event changes hands in MP the mail follows without a deploy.
 *
 * If we know who is reading, the form is already filled in: MP knows the
 * household, so "who's asking" is a row of faces to tap rather than two
 * fields to type. A guest still gets the plain fields.
 */
/**
 * WHERE A LINE UNDER THE NAME USED TO BE.
 *
 * It said "Ask Matt anything about this event", and the kicker above it
 * already says Questions, the chevron already says it opens, and the face
 * already says who. Three things saying the same thing, and the sentence was
 * the one adding nothing.
 *
 * What belongs there is Matt's title, and MP has nowhere to keep one: no
 * Job_Title on Contacts, no staff table, and `Occupation_Name` is what
 * somebody does for a living rather than what they do here. So the line is
 * gone rather than filled with a worse sentence, and the day there is a real
 * title to show, this is where it goes.
 */

export function ContactCard({
  apiBaseUrl,
  eventId,
  name,
  image,
  people,
  viewerContactId = null,
  variant = 'card',
  event,
}: {
  apiBaseUrl: string;
  eventId: number;
  name: string;
  image: string | null;
  /**
   * What the sheet wears across the top. Asking the host a question and
   * registering are the same night, so they open with the same band; without
   * this the questions sheet opened on a bare kicker and read as a different
   * product. The contact's own face and line move into the body, where they
   * still lead.
   */
  event: { image: string | null; title: string; start: string; end: string | null };
  /** The signed-in viewer's household. Empty for a guest. */
  people: ClientPerson[];
  /**
   * Which of them is holding the phone. The household comes back in MP's
   * order, not the reader's, so the viewer is found by id rather than taken
   * off the front of the list.
   */
  viewerContactId?: number | null;
  /**
   * 'row' draws it as one more labelled fact inside the card that already
   * holds Campus and Ministry, which is where it belongs: who answers for
   * this event is a fact about the event, not a section of its own. Her face
   * takes the icon's place — it IS the icon for this row — so the card keeps
   * one left edge and one indent.
   */
  variant?: 'card' | 'row';
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {variant === 'row' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="hover:bg-mist/60 flex w-full items-start gap-3 p-4 text-left transition"
        >
          {/* Same two columns as every other row: a mark, then the content.
              The face starts where the values above it start, so the card
              reads down one edge whether the row holds a word or a person. */}
          <span className="flex h-4 w-5 shrink-0 items-center justify-center">
            <MessageCircleQuestion className="text-teal h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="kicker mb-2 block">Questions</span>
            <span className="flex items-center gap-3">
              <Avatar name={name} image={image} size={36} />
              <span className="min-w-0 flex-1">
                <span className="text-navy block font-bold">{name}</span>
              </span>
            </span>
          </span>
          {/* Centred on the WHOLE row, not on the face. Inside the inner line
              it sat level with the name and low against a row whose top edge
              is the kicker, which reads as a chevron that slipped. */}
          <ChevronRight className="text-slate h-4 w-4 shrink-0 self-center" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="card lift flex w-full items-center gap-4 p-4 text-left"
        >
          <Avatar name={name} image={image} size={48} />
          <span className="min-w-0 flex-1">
            <span className="kicker block">Questions</span>
            <span className="text-navy mt-0.5 block text-base font-bold">{name}</span>
          </span>
          <ChevronRight className="text-slate h-4 w-4 shrink-0" />
        </button>
      )}

      <AskSheet
        apiBaseUrl={apiBaseUrl}
        eventId={eventId}
        open={open}
        onClose={() => setOpen(false)}
        name={name}
        image={image}
        people={people}
        viewerContactId={viewerContactId}
        event={event}
      />
    </>
  );
}

/**
 * The viewer's own seat in their household, or the first row when MP did not
 * say who is signed in. Null when there is no household at all, which is what
 * keeps a guest on the typed fields.
 */
function seatFor(people: ClientPerson[], viewerContactId: number | null): number | null {
  const self = people.find((p) => p.contactId === viewerContactId);
  return self?.contactId ?? people[0]?.contactId ?? null;
}

function AskSheet({
  apiBaseUrl,
  eventId,
  open,
  onClose,
  name,
  image,
  people,
  viewerContactId,
  event,
}: {
  apiBaseUrl: string;
  eventId: number;
  open: boolean;
  onClose: () => void;
  name: string;
  image: string | null;
  people: ClientPerson[];
  viewerContactId: number | null;
  /** What the sheet wears across the top. See the band in EventSheetBand. */
  event: { image: string | null; title: string; start: string; end: string | null };
}) {
  /**
   * Whose question this is: a household Contact_ID, or 'other' for somebody
   * MP has never met.
   *
   * Nearly always the person reading the page — a parent asking about their
   * daughter's retreat is still asking as themselves, and the answer comes
   * back to their address. So the viewer is ticked, and switching to a
   * daughter is one tap for the rare case that runs the other way.
   *
   * Seeded by id, not by position: `people` arrives in MP's order and the
   * viewer is not reliably at the front of it.
   */
  const [who, setWho] = useState<number | 'other'>(
    () => seatFor(people, viewerContactId) ?? 'other'
  );
  // Who is signed in is fetched separately from the event, so the household
  // can land after this sheet has already picked. Until somebody chooses for
  // themselves, the seed follows it in.
  const picked = useRef(false);
  useEffect(() => {
    if (picked.current) return;
    const seat = seatFor(people, viewerContactId);
    if (seat !== null) setWho(seat);
  }, [people, viewerContactId]);

  const choose = (seat: number | 'other') => {
    picked.current = true;
    setWho(seat);
    // A different person, a different address: the one that was being typed
    // belonged to whoever was ticked a moment ago.
    setChanging(false);
  };

  const mine = people.find((p) => p.contactId === who);
  const [typedName, setTypedName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [church, setChurch] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);

  // The address on file is the one thing that is often stale, so it stays
  // editable even when the name is settled by a face.
  const [edited, setEdited] = useState<Record<number, string>>({});
  const theirEmail = mine ? (edited[mine.contactId] ?? mine.email ?? '') : email;

  /**
   * NOBODY IS ASKED FOR SOMETHING THE CHURCH ALREADY KNOWS.
   *
   * Tick your own face and the address is already on the record — printing an
   * empty-looking box under it asks a signed-in person to retype what they
   * just proved they own, and an input that arrives pre-filled reads as a
   * thing to check rather than a thing that is settled. So it is a line of
   * text, and `Change` puts the box back for the one case that matters: an
   * address on file that has gone stale.
   *
   * Somebody MP has never met, or a household member with no address, still
   * gets the box straight away — there is nothing to show them instead.
   */
  const [changing, setChanging] = useState(false);
  const onFile = Boolean(mine && (mine.email ?? '').trim());
  const askForEmail = !mine || !onFile || changing;

  const field =
    'border-line bg-paper focus:border-teal mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none';
  const ready = Boolean(
    (mine || typedName.trim()) && theirEmail.trim().includes('@') && message.trim()
  );

  const send = async () => {
    if (!ready || state === 'sending') return;
    setState('sending');
    setError(null);
    try {
      await askQuestion(apiBaseUrl, eventId, {
        name: mine ? `${mine.firstName} ${mine.lastName}`.trim() : typedName.trim(),
        email: theirEmail.trim(),
        message: message.trim(),
        contactId: mine?.contactId,
        church,
      });
      setState('sent');
      setMessage('');
      // The button says Sent; the sheet gets out of the way. Two words
      // saying the same thing is one too many.
      window.setTimeout(() => {
        onClose();
        setState('idle');
      }, 1100);
    } catch (err) {
      setState('failed');
      setError(err instanceof Error ? err.message : 'Could not send it. Try again in a minute.');
    }
  };

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-lg"
      panelClassName="bg-ground text-ink md:rounded-3xl"
      noPanelPadding
      hideCloseButton
      header={
        <EventSheetBand
          image={event.image}
          title={event.title}
          start={event.start}
          end={event.end}
          onClose={onClose}
        />
      }
    >
      <SheetPage name="main">
        <div className="ed space-y-4 px-5 pt-5 pb-8">
          {/* Who this reaches, leading the sheet the way it led the row that
              opened it. Under the band rather than in it: the band is the
              event, and this is the person. */}
          <div className="flex items-center gap-3">
            <span className="flex h-4 w-5 shrink-0 items-center justify-center">
              <MessageCircleQuestion className="text-teal h-5 w-5" />
            </span>
            <p className="kicker">Questions</p>
          </div>
          <div className="flex items-center gap-3 pl-8">
            <Avatar name={name} image={image} size={44} />
            <div className="min-w-0">
              <p className="text-navy text-lg font-extrabold">{name}</p>
            </div>
          </div>
          {people.length > 0 && (
            <fieldset>
              <legend className="kicker mb-2">Who's asking</legend>
              <div className="flex flex-wrap gap-1.5">
                {people.map((p) => {
                  const on = who === p.contactId;
                  return (
                    <button
                      key={p.contactId}
                      type="button"
                      onClick={() => choose(p.contactId)}
                      aria-pressed={on}
                      className={`flex items-center gap-2 rounded-full border py-1 pr-3.5 pl-1 text-sm font-semibold transition ${
                        on
                          ? 'border-teal bg-teal-soft text-navy'
                          : 'border-line bg-paper text-slate-deep hover:shadow-md'
                      }`}
                    >
                      <Avatar name={`${p.firstName} ${p.lastName}`} image={p.image} size={24} />
                      {p.firstName}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => choose('other')}
                  aria-pressed={who === 'other'}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${
                    who === 'other'
                      ? 'border-teal bg-teal-soft text-navy'
                      : 'border-line bg-paper text-slate-deep hover:shadow-md'
                  }`}
                >
                  Someone else
                </button>
              </div>
            </fieldset>
          )}

          {!mine && (
            <label className="block">
              <span className="kicker">Your name</span>
              <input
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                maxLength={100}
                autoComplete="name"
                className={field}
              />
            </label>
          )}

          {askForEmail ? (
            <label className="block">
              <span className="kicker">Email</span>
              <input
                type="email"
                value={theirEmail}
                onChange={(e) =>
                  mine
                    ? setEdited({ ...edited, [mine.contactId]: e.target.value })
                    : setEmail(e.target.value)
                }
                maxLength={200}
                autoComplete="email"
                className={field}
              />
            </label>
          ) : (
            <div>
              <p className="kicker">Email</p>
              <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-navy text-sm font-semibold break-all">{theirEmail}</span>
                <button
                  type="button"
                  onClick={() => setChanging(true)}
                  className="text-slate hover:text-navy text-sm font-semibold underline underline-offset-2"
                >
                  Change
                </button>
              </div>
            </div>
          )}

          <label className="block">
            <span className="kicker">Your question</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={4000}
              className={field}
            />
          </label>

          {/* Honeypot. Invisible to people, filled by bots, checked server-side. */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden
            value={church}
            onChange={(e) => setChurch(e.target.value)}
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
          />

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              className="btn btn-primary"
              onClick={send}
              disabled={!ready || state === 'sending' || state === 'sent'}
            >
              {state === 'sending' ? 'Sending…' : state === 'sent' ? 'Sent' : 'Send'}
            </button>
            {error && <span className="text-rose text-sm font-semibold">{error}</span>}
          </div>
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
